import React, { useState, Fragment, Component } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CalendarIcon, ChevronDownIcon, ChevronUpIcon, ClockIcon, FileTextIcon, UsersIcon, HelpCircleIcon, InfoIcon, XIcon, CheckCircleIcon, AlertTriangleIcon, TrendingUpIcon, BarChart3Icon, TargetIcon } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
// --- Types ---
interface StudentActivity {
  id: number;
  name: string;
  status: 'Completed' | 'In Progress' | 'Not Started';
  score: number | null;
  totalScore?: number; // Add total possible score
  questionsAnswered?: number; // Add questions answered
  totalQuestions?: number; // Add total questions
  lastActivity: string;
  progressPct?: number;
}
interface AssessmentActivity {
  id: number;
  title: string;
  questionsCount: number;
  studentsAttempted: number;
  completionRate: number;
  avgScore: number;
  lastActivity: string;
  grade: string;
  subject: string;
  // Aggregated insights
  participationBreakdown: {
    attempted: number;
    completed: number;
    dropped: number;
  };
  scoreSpread: 'Mostly high' | 'Mixed' | 'Mostly low';
  medianTime?: string;
  // Student list for the drawer
  students: StudentActivity[];
}
// --- Mock Data ---
const MOCK_EDUCATOR = {
  id: 1,
  name: 'Jessica Martinez',
  email: 'jessica.m@school.edu',
  grades: ['Grade 10', 'Grade 11'],
  subjects: ['Spanish', 'French']
};
const MOCK_MONTHLY_SUMMARY = {
  month: 'March 2024',
  assessmentsWithActivity: 3,
  studentsAttempted: 52,
  avgCompletionRate: 88,
  avgScore: 79
};
const MOCK_ASSESSMENTS: AssessmentActivity[] = [{
  id: 101,
  title: 'Spanish Vocabulary: Unit 4 Review',
  questionsCount: 20,
  studentsAttempted: 28,
  completionRate: 92,
  avgScore: 85,
  lastActivity: 'Mar 28, 2:30 PM',
  grade: 'Grade 10',
  subject: 'Spanish',
  participationBreakdown: {
    attempted: 28,
    completed: 26,
    dropped: 2
  },
  scoreSpread: 'Mostly high',
  medianTime: '18 min',
  students: Array.from({
    length: 28
  }).map((_, i) => ({
    id: i,
    name: `Student ${i + 1}`,
    status: i < 26 ? 'Completed' : 'In Progress',
    score: i < 26 ? 80 + i % 15 : null,
    questionsAnswered: i < 26 ? 15 + i % 5 : 10 + i % 8,
    totalQuestions: 19,
    progressPct: i < 26 ? 100 : 45 + i % 30,
    lastActivity: 'Mar 28'
  }))
}, {
  id: 102,
  title: 'French Grammar: Past Tense',
  questionsCount: 15,
  studentsAttempted: 24,
  completionRate: 75,
  avgScore: 72,
  lastActivity: 'Mar 25, 10:15 AM',
  grade: 'Grade 11',
  subject: 'French',
  participationBreakdown: {
    attempted: 24,
    completed: 18,
    dropped: 6
  },
  scoreSpread: 'Mixed',
  medianTime: '22 min',
  students: []
}, {
  id: 103,
  title: 'Cultural Reading: Day of the Dead',
  questionsCount: 10,
  studentsAttempted: 30,
  completionRate: 95,
  avgScore: 88,
  lastActivity: 'Mar 15, 11:00 AM',
  grade: 'Grade 10',
  subject: 'Spanish',
  participationBreakdown: {
    attempted: 30,
    completed: 29,
    dropped: 1
  },
  scoreSpread: 'Mostly high',
  medianTime: '12 min',
  students: []
}];
// --- Components ---
const SummaryCard = ({
  label,
  value,
  subtext,
  icon: Icon,
  bgColor,
  iconColor,
  valueColor,
  tooltip
}: {
  label: string;
  value: string | number;
  subtext?: string;
  icon: React.ElementType;
  bgColor: string;
  iconColor: string;
  valueColor: string;
  tooltip?: string;
}) => <div className={`${bgColor} border-2 border-custom rounded-xl p-4 flex flex-col h-full relative overflow-hidden transition-all hover:shadow-md hover:border-opacity-50`} data-id="element-2735">
    <div className="flex justify-between items-start mb-3" data-id="element-2736">
      <div className={`w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm ${iconColor}`} data-id="element-2737">
        <Icon size={20} data-id="element-2738" />
      </div>
      {tooltip && <Tooltip.Root data-id="element-2739">
          <Tooltip.Trigger asChild data-id="element-2740">
            <div className="cursor-help p-1 hover:bg-black/5 rounded-full transition-colors" data-id="element-2741">
              <InfoIcon size={14} className="text-text-tertiary" data-id="element-2742" />
            </div>
          </Tooltip.Trigger>
          <Tooltip.Portal data-id="element-2743">
            <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-2 rounded-lg shadow-custom-lg max-w-xs z-50 text-xs text-text-secondary" sideOffset={5} data-id="element-2744">
              {tooltip}
              <Tooltip.Arrow className="fill-surface-elevated" data-id="element-2745" />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>}
    </div>

    <p className="text-xs font-medium text-text-secondary mb-1" data-id="element-2746">{label}</p>
    <div className={`text-2xl font-bold mb-1 ${valueColor}`} data-id="element-2747">{value}</div>
    {subtext && <p className="text-xs text-text-secondary opacity-80" data-id="element-2748">{subtext}</p>}
  </div>;
const StudentListDrawer = ({
  isOpen,
  onClose,
  assessmentTitle,
  students
}: {
  isOpen: boolean;
  onClose: () => void;
  assessmentTitle: string;
  students: StudentActivity[];
}) => {
  if (!isOpen) return null;
  // Separate students by status
  const completedStudents = students.filter(s => s.status === 'Completed');
  const inProgressStudents = students.filter(s => s.status === 'In Progress');
  const droppedStudents = students.filter(s => s.status === 'Dropped Off Early');
  return <AnimatePresence data-id="element-2749">
      {isOpen && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-2750">
          {/* Backdrop with fade animation */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} data-id="element-2751" />

          {/* Drawer with slide animation */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="relative w-full max-w-lg bg-surface-elevated h-full shadow-2xl border-l border-custom flex flex-col" data-id="element-2752">
            {/* Header */}
            <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1,
          duration: 0.3
        }} className="p-6 border-b border-custom bg-surface" data-id="element-2753">
              <div className="flex items-start justify-between mb-4" data-id="element-2754">
                <div className="flex-1" data-id="element-2755">
                  <h3 className="text-lg font-semibold text-text-primary mb-1" data-id="element-2756">
                    Student Activity
                  </h3>
                  <p className="text-sm text-text-secondary line-clamp-2" data-id="element-2757">
                    {assessmentTitle}
                  </p>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-surface-elevated rounded-lg transition-colors text-text-secondary hover:text-text-primary" aria-label="Close" data-id="element-2758">
                  <XIcon size={20} data-id="element-2759" />
                </button>
              </div>

              {/* Status Summary - Smaller cards with tooltips */}
              <div className="grid grid-cols-3 gap-2" data-id="element-2760">
                <Tooltip.Root data-id="element-2761">
                  <Tooltip.Trigger asChild data-id="element-2762">
                    <motion.div initial={{
                  opacity: 0,
                  scale: 0.8
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  delay: 0.15,
                  duration: 0.3
                }} className="bg-[#E8F5E9] rounded-lg p-3 text-center cursor-help" data-id="element-2763">
                      <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center mx-auto mb-1.5" data-id="element-2764">
                        <CheckCircleIcon size={16} className="text-[#00B75F]" data-id="element-2765" />
                      </div>
                      <p className="text-xs font-medium text-text-secondary mb-0.5" data-id="element-2766">
                        Completed
                      </p>
                      <p className="text-xl font-bold text-text-primary" data-id="element-2767">
                        {completedStudents.length}
                      </p>
                    </motion.div>
                  </Tooltip.Trigger>
                  <Tooltip.Portal data-id="element-2768">
                    <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-2 rounded-lg shadow-custom-lg max-w-xs z-[60]" sideOffset={5} data-id="element-2769">
                      <p className="text-xs font-semibold text-text-primary mb-1" data-id="element-2770">
                        Completed
                      </p>
                      <p className="text-xs text-text-secondary leading-relaxed" data-id="element-2771">
                        Students who finished 100% of the assessment questions
                      </p>
                      <Tooltip.Arrow className="fill-surface-elevated" data-id="element-2772" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>

                <Tooltip.Root data-id="element-2773">
                  <Tooltip.Trigger asChild data-id="element-2774">
                    <motion.div initial={{
                  opacity: 0,
                  scale: 0.8
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  delay: 0.2,
                  duration: 0.3
                }} className="bg-[#FFF3E0] rounded-lg p-3 text-center cursor-help" data-id="element-2775">
                      <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center mx-auto mb-1.5" data-id="element-2776">
                        <ClockIcon size={16} className="text-[#FF9D33]" data-id="element-2777" />
                      </div>
                      <p className="text-xs font-medium text-text-secondary mb-0.5" data-id="element-2778">
                        In Progress
                      </p>
                      <p className="text-xl font-bold text-text-primary" data-id="element-2779">
                        {inProgressStudents.length}
                      </p>
                    </motion.div>
                  </Tooltip.Trigger>
                  <Tooltip.Portal data-id="element-2780">
                    <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-2 rounded-lg shadow-custom-lg max-w-xs z-[60]" sideOffset={5} data-id="element-2781">
                      <p className="text-xs font-semibold text-text-primary mb-1" data-id="element-2782">
                        In Progress
                      </p>
                      <p className="text-xs text-text-secondary leading-relaxed" data-id="element-2783">
                        Students who started and completed at least 20% of the
                        assessment
                      </p>
                      <Tooltip.Arrow className="fill-surface-elevated" data-id="element-2784" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>

                <Tooltip.Root data-id="element-2785">
                  <Tooltip.Trigger asChild data-id="element-2786">
                    <motion.div initial={{
                  opacity: 0,
                  scale: 0.8
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  delay: 0.25,
                  duration: 0.3
                }} className="bg-[#FFEBEE] rounded-lg p-3 text-center cursor-help" data-id="element-2787">
                      <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center mx-auto mb-1.5" data-id="element-2788">
                        <AlertTriangleIcon size={16} className="text-red-500" data-id="element-2789" />
                      </div>
                      <p className="text-xs font-medium text-text-secondary mb-0.5" data-id="element-2790">
                        Dropped
                      </p>
                      <p className="text-xl font-bold text-text-primary" data-id="element-2791">
                        {droppedStudents.length}
                      </p>
                    </motion.div>
                  </Tooltip.Trigger>
                  <Tooltip.Portal data-id="element-2792">
                    <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-2 rounded-lg shadow-custom-lg max-w-xs z-[60]" sideOffset={5} data-id="element-2793">
                      <p className="text-xs font-semibold text-text-primary mb-1" data-id="element-2794">
                        Dropped Off Early
                      </p>
                      <p className="text-xs text-text-secondary leading-relaxed" data-id="element-2795">
                        Students who started but disengaged before completing
                        20% of the assessment
                      </p>
                      <Tooltip.Arrow className="fill-surface-elevated" data-id="element-2796" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </div>
            </motion.div>

            {/* Student List with staggered animation */}
            <div className="flex-1 overflow-y-auto bg-surface" data-id="element-2797">
              {students.length === 0 ? <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2,
            duration: 0.3
          }} className="flex flex-col items-center justify-center h-full text-center py-12 px-6" data-id="element-2798">
                  <FileTextIcon size={40} className="text-text-tertiary mb-3" data-id="element-2799" />
                  <p className="text-sm font-medium text-text-primary mb-1" data-id="element-2800">
                    No Student Data
                  </p>
                  <p className="text-xs text-text-secondary" data-id="element-2801">
                    No activity recorded yet
                  </p>
                </motion.div> : <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.2,
            duration: 0.3
          }} className="p-6 space-y-6" data-id="element-2802">
                  {/* Completed Students */}
                  {completedStudents.length > 0 && <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.3,
              duration: 0.3
            }} data-id="element-2803">
                      <div className="flex items-center gap-2 mb-3" data-id="element-2804">
                        <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-2805">
                          Completed ({completedStudents.length})
                        </h4>
                      </div>
                      <div className="bg-surface-elevated rounded-xl border border-custom overflow-hidden" data-id="element-2806">
                        <table className="w-full" data-id="element-2807">
                          <thead className="bg-surface border-b border-custom" data-id="element-2808">
                            <tr data-id="element-2809">
                              <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-2810">
                                Student Name
                              </th>
                              <th className="text-center px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-2811">
                                Total Score
                              </th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-2812">
                                Accuracy
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-custom" data-id="element-2813">
                            {completedStudents.map((student, index) => <motion.tr key={student.id} initial={{
                      opacity: 0,
                      x: -10
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      delay: 0.35 + index * 0.05,
                      duration: 0.2
                    }} className="hover:bg-surface transition-colors" data-id="element-2814">
                                <td className="px-4 py-3" data-id="element-2815">
                                  <p className="text-sm font-medium text-text-primary" data-id="element-2816">
                                    {student.name}
                                  </p>
                                </td>
                                <td className="px-4 py-3 text-center" data-id="element-2817">
                                  <span className="text-sm font-medium text-text-primary" data-id="element-2818">
                                    {student.questionsAnswered || 0}/
                                    {student.totalQuestions || 0}
                                  </span>
                                </td>
                                <td className="px-4 py-3" data-id="element-2819">
                                  <div className="flex items-center gap-2" data-id="element-2820">
                                    <div className="flex-1 max-w-[120px]" data-id="element-2821">
                                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-2822">
                                        <div className="h-full bg-[#00B75F] rounded-full transition-all" style={{
                                width: `${student.score || 0}%`
                              }} data-id="element-2823" />
                                      </div>
                                    </div>
                                    <span className="text-sm font-medium text-text-secondary whitespace-nowrap" data-id="element-2824">
                                      {student.score}%
                                    </span>
                                  </div>
                                </td>
                              </motion.tr>)}
                          </tbody>
                        </table>
                      </div>
                    </motion.div>}

                  {/* In Progress Students */}
                  {inProgressStudents.length > 0 && <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.4,
              duration: 0.3
            }} data-id="element-2825">
                      <div className="flex items-center gap-2 mb-3" data-id="element-2826">
                        <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-2827">
                          In Progress ({inProgressStudents.length})
                        </h4>
                      </div>
                      <div className="bg-surface-elevated rounded-xl border border-custom overflow-hidden" data-id="element-2828">
                        <table className="w-full" data-id="element-2829">
                          <thead className="bg-surface border-b border-custom" data-id="element-2830">
                            <tr data-id="element-2831">
                              <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-2832">
                                Student Name
                              </th>
                              <th className="text-center px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-2833">
                                Total Score
                              </th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-2834">
                                Accuracy
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-custom" data-id="element-2835">
                            {inProgressStudents.map((student, index) => <motion.tr key={student.id} initial={{
                      opacity: 0,
                      x: -10
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      delay: 0.45 + index * 0.05,
                      duration: 0.2
                    }} className="hover:bg-surface transition-colors" data-id="element-2836">
                                <td className="px-4 py-3" data-id="element-2837">
                                  <p className="text-sm font-medium text-text-primary" data-id="element-2838">
                                    {student.name}
                                  </p>
                                </td>
                                <td className="px-4 py-3 text-center" data-id="element-2839">
                                  <span className="text-sm font-medium text-text-primary" data-id="element-2840">
                                    {student.questionsAnswered || 0}/
                                    {student.totalQuestions || 0}
                                  </span>
                                </td>
                                <td className="px-4 py-3" data-id="element-2841">
                                  <div className="flex items-center gap-2" data-id="element-2842">
                                    <div className="flex-1 max-w-[120px]" data-id="element-2843">
                                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-2844">
                                        <div className="h-full bg-[#00B75F] rounded-full transition-all" style={{
                                width: `${student.progressPct || 0}%`
                              }} data-id="element-2845" />
                                      </div>
                                    </div>
                                    <span className="text-sm font-medium text-text-secondary whitespace-nowrap" data-id="element-2846">
                                      {student.progressPct || 0}%
                                    </span>
                                  </div>
                                </td>
                              </motion.tr>)}
                          </tbody>
                        </table>
                      </div>
                    </motion.div>}

                  {/* Dropped Off Early Students */}
                  {droppedStudents.length > 0 && <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.5,
              duration: 0.3
            }} data-id="element-2847">
                      <div className="flex items-center gap-2 mb-3" data-id="element-2848">
                        <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-2849">
                          Dropped Off ({droppedStudents.length})
                        </h4>
                      </div>

                      {/* Alert Banner */}
                      <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.55,
                duration: 0.3
              }} className="bg-[#FFEBEE] border border-[#FFCDD2] rounded-xl p-4 mb-3" data-id="element-2850">
                        <div className="flex gap-3" data-id="element-2851">
                          <AlertTriangleIcon size={18} className="text-red-500 flex-shrink-0 mt-0.5" data-id="element-2852" />
                          <div data-id="element-2853">
                            <p className="text-xs font-semibold text-red-900 mb-1" data-id="element-2854">
                              High Drop-off Rate
                            </p>
                            <p className="text-xs text-red-700 leading-relaxed" data-id="element-2855">
                              Students disengaged before 20% completion
                            </p>
                          </div>
                        </div>
                      </motion.div>

                      <div className="bg-surface-elevated rounded-xl border border-custom overflow-hidden" data-id="element-2856">
                        <table className="w-full" data-id="element-2857">
                          <thead className="bg-surface border-b border-custom" data-id="element-2858">
                            <tr data-id="element-2859">
                              <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-2860">
                                Student Name
                              </th>
                              <th className="text-center px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-2861">
                                Total Score
                              </th>
                              <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-2862">
                                Accuracy
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-custom" data-id="element-2863">
                            {droppedStudents.map((student, index) => <motion.tr key={student.id} initial={{
                      opacity: 0,
                      x: -10
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      delay: 0.6 + index * 0.05,
                      duration: 0.2
                    }} className="hover:bg-surface transition-colors" data-id="element-2864">
                                <td className="px-4 py-3" data-id="element-2865">
                                  <p className="text-sm font-medium text-text-primary" data-id="element-2866">
                                    {student.name}
                                  </p>
                                </td>
                                <td className="px-4 py-3 text-center" data-id="element-2867">
                                  <span className="text-sm font-medium text-text-primary" data-id="element-2868">
                                    {student.questionsAnswered || 0}/
                                    {student.totalQuestions || 0}
                                  </span>
                                </td>
                                <td className="px-4 py-3" data-id="element-2869">
                                  <div className="flex items-center gap-2" data-id="element-2870">
                                    <div className="flex-1 max-w-[120px]" data-id="element-2871">
                                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-2872">
                                        <div className="h-full bg-red-500 rounded-full transition-all" style={{
                                width: `${student.progressPct || 0}%`
                              }} data-id="element-2873" />
                                      </div>
                                    </div>
                                    <span className="text-sm font-medium text-text-secondary whitespace-nowrap" data-id="element-2874">
                                      {student.progressPct || 0}%
                                    </span>
                                  </div>
                                </td>
                              </motion.tr>)}
                          </tbody>
                        </table>
                      </div>
                    </motion.div>}
                </motion.div>}
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
};
export function EducatorMonthlyDetail() {
  const navigate = useNavigate();
  const {
    educatorId,
    month
  } = useParams();
  const [selectedAssessmentForDrawer, setSelectedAssessmentForDrawer] = useState<AssessmentActivity | null>(null);
  return <Tooltip.Provider delayDuration={300} data-id="element-2875">
      <div className="flex flex-col min-h-screen bg-background" data-id="element-2876">
        <Header data-id="element-2877" />
        <div className="flex flex-1 overflow-hidden" data-id="element-2878">
          <div className="flex-1 flex flex-col min-w-0 w-full overflow-y-auto" data-id="element-2879">
            <main className="flex-1 max-w-full mx-auto w-full px-4 py-6" data-id="element-2880">
              {/* 1. Educator Context Header */}
              <motion.div initial={{
              opacity: 0,
              y: -20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3
            }} className="mb-6 max-w-7xl mx-auto" data-id="element-2881">
                <button onClick={() => navigate('/organization/overview')} className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors mb-3" data-id="element-2882">
                  <ArrowLeftIcon size={16} data-id="element-2883" />
                  Back to Overview
                </button>

                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3" data-id="element-2884">
                  <div data-id="element-2885">
                    <h1 className="text-2xl font-bold text-text-primary mb-1" data-id="element-2886">
                      {MOCK_EDUCATOR.name}
                    </h1>
                    <div className="flex items-center gap-2 text-sm text-text-secondary mb-2" data-id="element-2887">
                      <span className="text-xs" data-id="element-2888">{MOCK_EDUCATOR.email}</span>
                      <span className="w-1 h-1 rounded-full bg-gray-300" data-id="element-2889" />
                      <div className="flex gap-1.5" data-id="element-2890">
                        {MOCK_EDUCATOR.grades.map(g => <span key={g} className="bg-surface-elevated border border-custom px-1.5 py-0.5 rounded text-xs" data-id="element-2891">
                            {g}
                          </span>)}
                        {MOCK_EDUCATOR.subjects.map(s => <span key={s} className="bg-surface-elevated border border-custom px-1.5 py-0.5 rounded text-xs" data-id="element-2892">
                            {s}
                          </span>)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 bg-surface-elevated border border-custom px-3 py-1.5 rounded-lg text-text-primary font-medium text-sm" data-id="element-2893">
                    <CalendarIcon size={14} className="text-text-secondary" data-id="element-2894" />
                    {MOCK_MONTHLY_SUMMARY.month}
                  </div>
                </div>
              </motion.div>

              {/* 2. Monthly Summary Strip */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3,
              delay: 0.1
            }} className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6 max-w-7xl mx-auto" data-id="element-2895">
                <SummaryCard label="Assessments with activity" value={MOCK_MONTHLY_SUMMARY.assessmentsWithActivity} icon={FileTextIcon} bgColor="bg-[#E8F5E9]" iconColor="text-[#00B75F]" valueColor="text-[#00B75F]" tooltip="Total number of assessments that had student activity during this month" data-id="element-2896" />
                <SummaryCard label="Students attempted" value={MOCK_MONTHLY_SUMMARY.studentsAttempted} subtext="unique students" icon={UsersIcon} bgColor="bg-[#E3F2FD]" iconColor="text-[#2563EB]" valueColor="text-[#2563EB]" tooltip="Number of unique students who attempted at least one assessment" data-id="element-2897" />
                <SummaryCard label="Avg completion rate" value={`${MOCK_MONTHLY_SUMMARY.avgCompletionRate}%`} icon={CheckCircleIcon} bgColor="bg-[#E8F5E9]" iconColor="text-[#00B75F]" valueColor="text-[#00B75F]" tooltip="Average percentage of questions answered across all attempts" data-id="element-2898" />
                <SummaryCard label="Avg score" value={`${MOCK_MONTHLY_SUMMARY.avgScore}%`} subtext="excludes incomplete" icon={BarChart3Icon} bgColor="bg-[#FFF3E0]" iconColor="text-[#FF9D33]" valueColor="text-[#FF9D33]" tooltip="Average score achieved on completed assessments" data-id="element-2899" />
              </motion.div>

              {/* 3. Assessment-Level Activity Table */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3,
              delay: 0.2
            }} className="bg-surface-elevated border-2 border-[#00B75F] border-opacity-20 rounded-xl overflow-hidden mb-6 max-w-7xl mx-auto" data-id="element-2900">
                <div className="px-4 py-3 border-b border-custom bg-gradient-to-r from-[#00B75F]/5 to-transparent" data-id="element-2901">
                  <h2 className="text-base font-bold text-text-primary" data-id="element-2902">
                    Assessment Activity
                  </h2>
                  <p className="text-xs text-text-secondary" data-id="element-2903">
                    Detailed breakdown of student engagement per assessment
                  </p>
                </div>

                <div className="overflow-x-auto" data-id="element-2904">
                  <table className="w-full" data-id="element-2905">
                    <thead className="bg-[#EEF2FF] border-b border-custom" data-id="element-2906">
                      <tr data-id="element-2907">
                        <th className="text-left px-4 py-2 text-xs font-bold text-text-secondary uppercase tracking-wide" data-id="element-2908">
                          Assessment
                        </th>
                        <th className="text-center px-4 py-2 text-xs font-bold text-text-secondary uppercase tracking-wide" data-id="element-2909">
                          Questions
                        </th>
                        <th className="text-center px-4 py-2 text-xs font-bold text-text-secondary uppercase tracking-wide" data-id="element-2910">
                          <Tooltip.Root data-id="element-2911">
                            <Tooltip.Trigger asChild data-id="element-2912">
                              <div className="flex items-center justify-center gap-1 cursor-help" data-id="element-2913">
                                Students
                                <InfoIcon size={12} className="text-text-tertiary" data-id="element-2914" />
                              </div>
                            </Tooltip.Trigger>
                            <Tooltip.Portal data-id="element-2915">
                              <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-2 rounded-lg shadow-custom-lg max-w-xs z-50 text-xs text-text-secondary" sideOffset={5} data-id="element-2916">
                                <p className="text-xs font-semibold text-text-primary mb-1" data-id="element-2917">
                                  Students Per Assessment
                                </p>
                                <p className="text-xs text-text-secondary leading-relaxed" data-id="element-2918">
                                  Number of students who attempted this specific
                                  assessment (same student may appear in
                                  multiple assessments)
                                </p>
                                <Tooltip.Arrow className="fill-surface-elevated" data-id="element-2919" />
                              </Tooltip.Content>
                            </Tooltip.Portal>
                          </Tooltip.Root>
                        </th>
                        <th className="text-left px-4 py-2 text-xs font-bold text-text-secondary uppercase tracking-wide" data-id="element-2920">
                          <Tooltip.Root data-id="element-2921">
                            <Tooltip.Trigger asChild data-id="element-2922">
                              <div className="flex items-center gap-1 cursor-help" data-id="element-2923">
                                Completion
                                <InfoIcon size={12} className="text-text-tertiary" data-id="element-2924" />
                              </div>
                            </Tooltip.Trigger>
                            <Tooltip.Portal data-id="element-2925">
                              <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-2 rounded-lg shadow-custom-lg max-w-xs z-50 text-xs text-text-secondary" sideOffset={5} data-id="element-2926">
                                Average completion rate across all attempts
                                <Tooltip.Arrow className="fill-surface-elevated" data-id="element-2927" />
                              </Tooltip.Content>
                            </Tooltip.Portal>
                          </Tooltip.Root>
                        </th>
                        <th className="text-left px-4 py-2 text-xs font-bold text-text-secondary uppercase tracking-wide" data-id="element-2928">
                          <Tooltip.Root data-id="element-2929">
                            <Tooltip.Trigger asChild data-id="element-2930">
                              <div className="flex items-center gap-1 cursor-help" data-id="element-2931">
                                Score
                                <InfoIcon size={12} className="text-text-tertiary" data-id="element-2932" />
                              </div>
                            </Tooltip.Trigger>
                            <Tooltip.Portal data-id="element-2933">
                              <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-2 rounded-lg shadow-custom-lg max-w-xs z-50 text-xs text-text-secondary" sideOffset={5} data-id="element-2934">
                                Average score across completed attempts
                                <Tooltip.Arrow className="fill-surface-elevated" data-id="element-2935" />
                              </Tooltip.Content>
                            </Tooltip.Portal>
                          </Tooltip.Root>
                        </th>
                        <th className="text-center px-4 py-2 text-xs font-bold text-text-secondary uppercase tracking-wide" data-id="element-2936">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-custom" data-id="element-2937">
                      {MOCK_ASSESSMENTS.length === 0 ? <tr data-id="element-2938">
                          <td colSpan={6} className="px-4 py-8 text-center text-text-secondary" data-id="element-2939">
                            No assessments had student activity during this
                            month.
                          </td>
                        </tr> : MOCK_ASSESSMENTS.map((assessment, index) => {
                      // Helper function to get completion rate color (matching layer 1)
                      const getCompletionColor = (rate: number) => {
                        if (rate >= 80) return 'text-[#00B75F] font-semibold';
                        if (rate >= 50) return 'text-text-primary font-medium';
                        return 'text-[#FF9D33] font-medium';
                      };
                      return <motion.tr key={assessment.id} initial={{
                        opacity: 0,
                        x: -20
                      }} animate={{
                        opacity: 1,
                        x: 0
                      }} transition={{
                        duration: 0.2,
                        delay: 0.3 + index * 0.03
                      }} className="hover:bg-[#00B75F] hover:bg-opacity-5 transition-colors group" data-id="element-2940">
                              <td className="px-4 py-2.5" data-id="element-2941">
                                <div className="flex items-start gap-2.5" data-id="element-2942">
                                  <div className="p-2 bg-[#00B75F] bg-opacity-10 rounded-lg text-[#00B75F] flex-shrink-0" data-id="element-2943">
                                    <FileTextIcon size={16} data-id="element-2944" />
                                  </div>
                                  <div className="flex flex-col gap-1.5" data-id="element-2945">
                                    <span className="font-semibold text-sm text-text-primary" data-id="element-2946">
                                      {assessment.title}
                                    </span>
                                    <div className="flex gap-1.5" data-id="element-2947">
                                      <span className="inline-flex items-center px-1.5 py-0.5 bg-surface rounded text-xs font-medium text-text-secondary border border-custom" data-id="element-2948">
                                        {assessment.grade}
                                      </span>
                                      <span className="inline-flex items-center px-1.5 py-0.5 bg-surface rounded text-xs font-medium text-text-secondary border border-custom" data-id="element-2949">
                                        {assessment.subject}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-2.5 text-center" data-id="element-2950">
                                <span className="text-sm font-medium text-text-secondary" data-id="element-2951">
                                  {assessment.questionsCount}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-center" data-id="element-2952">
                                <span className="text-sm font-semibold text-text-primary" data-id="element-2953">
                                  {assessment.studentsAttempted}
                                </span>
                              </td>
                              <td className="px-4 py-2.5" data-id="element-2954">
                                <span className={`text-sm font-semibold ${getCompletionColor(assessment.completionRate)}`} data-id="element-2955">
                                  {assessment.completionRate}%
                                </span>
                              </td>
                              <td className="px-4 py-2.5" data-id="element-2956">
                                <span className="text-sm font-normal text-[#9CA3AF]" data-id="element-2957">
                                  {assessment.avgScore}%
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-center" data-id="element-2958">
                                <button onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedAssessmentForDrawer(assessment);
                          }} className="inline-flex items-center gap-1.5 px-3 py-2 bg-surface-elevated border-2 border-[#00B75F] text-[#00B75F] rounded-lg text-xs font-semibold hover:bg-[#00B75F] hover:text-white transition-colors active:scale-95" data-id="element-2959">
                                  <UsersIcon size={14} data-id="element-2960" />
                                  <span className="hidden sm:inline" data-id="element-2961">
                                    View Students
                                  </span>
                                  <span className="sm:hidden" data-id="element-2962">View</span>
                                </button>
                              </td>
                            </motion.tr>;
                    })}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </main>
            <Footer data-id="element-2963" />
          </div>
        </div>

        {/* Student List Drawer */}
        <StudentListDrawer isOpen={!!selectedAssessmentForDrawer} onClose={() => setSelectedAssessmentForDrawer(null)} assessmentTitle={selectedAssessmentForDrawer?.title || ''} students={selectedAssessmentForDrawer?.students || []} data-id="element-2964" />
      </div>
    </Tooltip.Provider>;
}